/* eslint-disable complexity */

import { useViewportState } from './use-viewport-state';
import { ViewportServiceClass } from '../../viewport-service';

export interface ShowViewportInfoProps {
	viewportService: ViewportServiceClass;
}

/**
 * Renders the full viewport info when everything is initialized
 */
const renderFullViewportInfo = (params: {
	containerSize: any;
	viewerSize: any;
	viewportState: any;
	viewBox: string[] | null;
	viewportService: ViewportServiceClass;
}) => {
	const { viewBox, containerSize, viewerSize, viewportState, viewportService } = params;

	return (
		<div style={{
			position: 'absolute',
			top: '20px',
			right: '20px',
			zIndex: 10,
			maxWidth: '400px',
			backgroundColor: 'rgba(255, 255, 255, 0.55)',
			border: '1px solid #ccc',
			borderRadius: '8px',
			padding: '15px',
			boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
		}}>
			<h3 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>Viewport Info</h3>
			<div style={{ fontSize: '12px' }}>
				<div><strong>Zoom:</strong> {(viewportState.zoom * 100).toFixed(0)}%</div>
				<div><strong>Rotation:</strong> {viewportState.rotation.toFixed(1)}°</div>
				<div><strong>Pan X:</strong> {viewportState.xPan.toFixed(2)}</div>
				<div><strong>Pan Y:</strong> {viewportState.yPan.toFixed(2)}</div>
				<div><strong>Diameter:</strong> {viewerSize.diameter.toFixed(2)}</div>
				<div><strong>Container Size:</strong> {containerSize.width} × {containerSize.height}</div>
				<div><strong>Viewer Size:</strong> {viewerSize.width.toFixed(2)} × {viewerSize.height.toFixed(2)}</div>
				<div><strong>ViewBox Size:</strong> {viewBox ? `${viewBox[2]} x ${viewBox[3]}` : 'Initializing...'}</div>
				<div><strong>ViewBox Center:</strong> {viewBox ? `${viewBox[0]}, ${viewBox[1]}` : 'Initializing...'}</div>

				{/* Rotation History */}
				<div style={{ marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
					<div><strong>Rotation History:</strong> {viewportState.rotationHistory?.length || 0} entries</div>
					{viewportState.rotationHistory && viewportState.rotationHistory.length > 0 && (
						<div style={{ fontSize: '11px', marginLeft: '10px', color: '#666' }}>
							{viewportState.rotationHistory.map((entry, index) => (
								<div key={index}>
									Entry {index + 1}: {entry.rotation.toFixed(1)}° at ({entry.panX.toFixed(1)}, {entry.panY.toFixed(1)})
								</div>
							))}
						</div>
					)}
				</div>

				{/* Equivalent Rotation */}
				<div style={{ marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
					<div><strong>Equivalent Rotation:</strong></div>
					{(() => {
						try {
							const equivalent = viewportService.actions.getEquivalentRotation();
							return (
								<div style={{ fontSize: '11px', marginLeft: '10px', color: '#666' }}>
									<div>Angle: {equivalent.angleDegrees.toFixed(2)}° ({equivalent.angleRadians.toFixed(4)} rad)</div>
									<div>Center: ({equivalent.centerX.toFixed(2)}, {equivalent.centerY.toFixed(2)})</div>
									<div>Pure Translation: {equivalent.isPureTranslation ? 'Yes' : 'No'}</div>
								</div>
							);
						} catch (error) {
							return <div style={{ fontSize: '11px', marginLeft: '10px', color: '#666' }}>Error calculating equivalent rotation</div>;
						}
					})()}
				</div>
			</div>
		</div>
	);
};

export const ShowViewportInfo = (props: ShowViewportInfoProps) => {
	const { viewportService } = props;
	const { containerSize, viewerSize, viewportState, viewBox } = useViewportState(viewportService);

	// Check if both viewerSize and containerSize are properly initialized
	const isViewerInitialized = viewerSize && viewerSize.diameter && viewerSize.width && viewerSize.height;
	const isContainerInitialized = containerSize && containerSize.width > 0 && containerSize.height > 0;
	const isFullyInitialized = isViewerInitialized && isContainerInitialized;

	// Show initialization state if not fully initialized
	if (!isFullyInitialized) {
		return (
			<div style={{
				position: 'absolute',
				top: '20px',
				right: '20px',
				zIndex: 10,
				maxWidth: '400px',
				backgroundColor: 'rgba(255, 255, 255, 0.55)',
				border: '1px solid #ccc',
				borderRadius: '8px',
				padding: '15px',
				boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
			}}>
				<h3 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>Viewport Info</h3>
				<div style={{ fontSize: '12px', color: '#666' }}>
					<div>Initializing...</div>
					{!isViewerInitialized && <div>• Waiting for viewer size...</div>}
					{!isContainerInitialized && <div>• Waiting for container size...</div>}
					{isViewerInitialized && !isContainerInitialized && (
						<div>• Container: {containerSize?.width || 0} × {containerSize?.height || 0}</div>
					)}
					{!isViewerInitialized && isContainerInitialized && (
						<div>• Container: {containerSize.width} × {containerSize.height}</div>
					)}

					{/* Show rotation history even during initialization */}
					<div style={{ marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
						<div><strong>Rotation History:</strong> {viewportState.rotationHistory?.length || 0} entries</div>
						{viewportState.rotationHistory && viewportState.rotationHistory.length > 0 && (
							<div style={{ fontSize: '11px', marginLeft: '10px', color: '#666' }}>
								{viewportState.rotationHistory.map((entry, index) => (
									<div key={index}>
										Entry {index + 1}: {entry.rotation.toFixed(1)}° at ({entry.panX.toFixed(1)}, {entry.panY.toFixed(1)})
									</div>
								))}
							</div>
						)}
					</div>

					{/* Equivalent Rotation */}
					<div style={{ marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
						<div><strong>Equivalent Rotation:</strong></div>
						{(() => {
							try {
								const equivalent = viewportService.actions.getEquivalentRotation();
								return (
									<div style={{ fontSize: '11px', marginLeft: '10px', color: '#666' }}>
										<div>Angle: {equivalent.angleDegrees.toFixed(2)}° ({equivalent.angleRadians.toFixed(4)} rad)</div>
										<div>Center: ({equivalent.centerX.toFixed(2)}, {equivalent.centerY.toFixed(2)})</div>
										<div>Pure Translation: {equivalent.isPureTranslation ? 'Yes' : 'No'}</div>
									</div>
								);
							} catch (error) {
								return <div style={{ fontSize: '11px', marginLeft: '10px', color: '#666' }}>Error calculating equivalent rotation</div>;
							}
						})()}
					</div>
				</div>
			</div>
		);
	}

	const viewBoxArray = viewBox?.split(',')
		.map((n) => Number(n).toFixed(2)) ?? null;
	return renderFullViewportInfo({ viewBox: viewBoxArray, containerSize, viewerSize, viewportState, viewportService });
};
